// Inertia-Scrolling (iOS-Style) für Touch-Gesten.
// Nach dem Finger-Release läuft ein RAF-Loop, der die zuletzt gemessene
// Geschwindigkeit pro Frame exponentiell dämpft und scrollByPixels füttert —
// so lange bis Velocity unter Schwelle fällt, der Rand erreicht ist,
// oder von außen cancel() aufgerufen wird (neuer Touch, Wheel, Tastatur).
//
// Die Referenz-Framerate ist 60 fps; bei langsameren Frames skalieren wir
// den Decay-Faktor mit dtMs / 16.67, damit sich das Gefühl unabhängig von
// der tatsächlichen Framerate konstant anfühlt.

import { TIMING } from './config.js'
import { scrollProfiler } from './ScrollProfiler.js'

const FRAME_MS_60FPS = 1000 / 60

export class InertiaController {
    /**
     * @param {{ scrollByPixels: (px: number) => void }} scrollTarget - Objekt mit scrollByPixels-Methode
     *   (EditorView oder CoreView — beide haben den Pixel-Pfad).
     * @param {() => void} [onFrame] - optionaler Callback pro Frame (z.B. layout.isDirty = true)
     */
    constructor(scrollTarget, onFrame = null) {
        this.target = scrollTarget
        this.onFrame = onFrame
        this._rafId = null
        this._velocity = 0     // px/ms, positiv = nach unten
        this._lastT = 0
    }

    /**
     * Startet den Inertia-Loop mit einer initialen Geschwindigkeit.
     * @param {number} velocityPxPerMs - Richtung + Betrag (positiv = nach unten)
     */
    start(velocityPxPerMs) {
        if (!velocityPxPerMs || Math.abs(velocityPxPerMs) < TIMING.inertiaMinVelocity) return
        this.cancel()
        this._velocity = velocityPxPerMs
        this._lastT = performance.now()
        this._rafId = requestAnimationFrame(this._tick)
    }

    /**
     * Bricht einen laufenden Inertia-Loop ab (z.B. bei neuem Touch oder Wheel).
     */
    cancel() {
        if (this._rafId !== null) {
            cancelAnimationFrame(this._rafId)
            this._rafId = null
        }
        this._velocity = 0
    }

    /**
     * Läuft Inertia gerade?
     */
    isRunning() {
        return this._rafId !== null
    }

    _tick = () => {
        const now = performance.now()
        const dtMs = Math.min(64, now - this._lastT)  // cap bei 64ms gegen Frame-Stalls
        this._lastT = now

        scrollProfiler.noteInertiaTick()

        // Pixel-Schritt dieses Frames
        const stepPx = this._velocity * dtMs

        // Position VOR dem Scroll merken — wenn der Scroll durch Rand-Clamping
        // nicht mehr voranschreitet, stoppen wir (kein Overscroll-Flackern)
        const beforeOffset = this._snapshotScrollState()
        this.target.scrollByPixels(stepPx)
        const afterOffset = this._snapshotScrollState()
        const didMove = beforeOffset !== afterOffset

        // Exponential decay — an dtMs normalisiert
        const decay = Math.pow(TIMING.inertiaDecayPerFrame, dtMs / FRAME_MS_60FPS)
        this._velocity *= decay

        if (this.onFrame) this.onFrame()

        if (!didMove || Math.abs(this._velocity) < TIMING.inertiaMinVelocity) {
            this._rafId = null
            this._velocity = 0
            return
        }
        this._rafId = requestAnimationFrame(this._tick)
    }

    /**
     * Scroll-Position als vergleichbare Zahl (für Bewegungs-Detection am Rand).
     * Proportional nutzt _viewportPixelOffset (sub-pixel), Monospace nutzt
     * _monoScrollAccumulator (sub-line) — nur eines ist zur Zeit aktiv, daher reicht Addition.
     */
    _snapshotScrollState() {
        const t = this.target
        return (t.viewportStart || 0) * 1e6
            + (t._viewportPixelOffset || 0)
            + (t._monoScrollAccumulator || 0)
    }
}

/**
 * Berechnet eine Velocity aus Touch-Samples — als Peak ueber alle Sample-Paare
 * im inertiaVelocitySampleWindowMs-Fenster vor dem letzten.
 *
 * Warum Peak und nicht first-last? Auf iPhone (insbesondere ProMotion 120Hz)
 * verlangsamt der User den Finger oft 30–80 ms vor dem Lift („Endbremse"). Mit
 * first-last wuerde dann der niedrige End-Wert die ganze Geschwindigkeit
 * dominieren — das Resultat liegt unter `inertiaMinVelocity` und Inertia
 * startet gar nicht (Boris-Bug: „rollt nicht weiter" auf iPhone, iPad OK).
 * Peak filtert die Endbremse heraus, behaelt die wahre Wisch-Geschwindigkeit.
 *
 * Bei echtem User-Stopp (Finger steht 100+ ms still) sind alle Sample-Paare
 * im Window y-stabil → Peak = 0 → keine Inertia (korrekt).
 *
 * @param {Array<{t: number, y: number}>} samples - chronologisch sortiert
 * @returns {number} Velocity in px/ms (positiv = Finger nach oben = Inhalt nach unten)
 */
export function computeVelocity(samples) {
    if (!samples || samples.length < 2) return 0
    const last = samples[samples.length - 1]
    const windowStart = last.t - TIMING.inertiaVelocitySampleWindowMs

    // Peak-Magnitude wird durch das Fenster begrenzt; bei nur 2 Samples (und
    // beide ausserhalb des Fensters waeren) reicht die einzige Pair-Berechnung
    // als Fallback — derselbe Edge-Case, den der alte Algorithmus mit
    // `firstIdx = Math.max(0, length - 2)` deckte.
    let peakVelocity = 0
    let peakMag = 0
    for (let i = samples.length - 1; i > 0; i--) {
        const a = samples[i - 1]
        const b = samples[i]
        // Beide Samples ausserhalb des Fensters → keine weiteren relevanten
        // Pairs. Wenn `b` schon ausserhalb ist, ist `a` erst recht aelter.
        if (b.t < windowStart && i < samples.length - 1) break
        const dt = b.t - a.t
        if (dt <= 0) continue
        const v = (a.y - b.y) / dt
        const mag = Math.abs(v)
        if (mag > peakMag) {
            peakMag = mag
            peakVelocity = v
        }
    }
    return peakVelocity
}
